import React from 'react';
import clsx from 'clsx';
import {faCloudUpload} from '@fortawesome/pro-solid-svg-icons';
import {
	EmptyStateFallbackMessage,
	type EmptyStateFallbackMessageProps,
} from '@netx/core-components/EmptyStateFallbackMessage';
import {FallbackMessage} from '@netx/core-components/FallbackMessage';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {useUploaderDropzoneProvider} from '../../contexts';
import {UploadFilesDropZoneLabel} from '../../locales';
import {
	getUploadDropzoneUtilityClass,
	UPLOAD_DROPZONE_CLASS_NAME,
	type UploadDropzoneClasses,
	uploadDropzoneClasses,
} from './uploadDropzoneClasses';

type UploadDropzoneOwnerState = WithClasses<UploadDropzoneClasses>;

const useUtilityClasses = (ownerState: UploadDropzoneOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		splash: ['splash'],
	};
	return composeClasses(slots, getUploadDropzoneUtilityClass, classes);
};

const UploadDropzoneRoot = styled('div', {
	name: UPLOAD_DROPZONE_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	position: 'relative',
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	'&.dragEntered': {
		[`& .${uploadDropzoneClasses.splash}`]: {
			borderColor: theme.palette.primary.main,
			backgroundColor: theme.palette.custom.background.light,
		},
	},
}));

const UploadDropZoneSplashWrapper = styled('div', {
	name: UPLOAD_DROPZONE_CLASS_NAME,
	slot: 'SplashWrapper',
})(({theme}) => ({
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	// padding: theme.spacing(2),
	alignItems: 'center',
	justifyContent: 'center',
}));

const UploadDropzoneSplash = styled(EmptyStateFallbackMessage, {
	name: UPLOAD_DROPZONE_CLASS_NAME,
	slot: 'Splash',
})(({theme}) => ({
	background: 'transparent',
	flex: '0 0 auto',
	border: `3px dashed ${theme.palette.divider}`,
	borderRadius: theme.shape.borderRadius,
	display: 'flex',
	flexDirection: 'column',
	width: 600,
	maxWidth: `calc(100% - 32px)`,
	maxHeight: `calc(100% - 32px)`,
	[theme.breakpoints.down('sm')]: {
		flex: '1 1 auto',
	},
}));

const UploadDropZoneDragOverlayWrapper = styled('div', {
	name: UPLOAD_DROPZONE_CLASS_NAME,
	slot: 'SplashWrapper',
})(({theme}) => ({
	background: theme.palette.custom.background.backdrop,
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(2),
	position: 'absolute',
	pointerEvents: 'none',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	zIndex: 3,
}));

const UploadDropzoneDragOverlay = styled(
	FallbackMessage,
	{},
)(({theme}) => ({
	border: `3px dashed ${theme.palette.primary.main}`,
	borderRadius: theme.shape.borderRadius,
	background: 'transparent',
}));

export interface UploadDropzoneProps
	extends WithClasses<
		UploadDropzoneClasses,
		React.PropsWithChildren<EmptyStateFallbackMessageProps>
	> {
	disabled?: boolean;
	// icon?: IconDefinition;
	fileLimitationText?: string;
	title?: string;
	selectFileLabel?: string;
	useDragOverlayOnly?: boolean;
}

export const UploadDropzone = ({
	children,
	classes: _classes,
	className,
	disabled,
	fileLimitationText,
	icon,
	selectFileLabel,
	title,
	useDragOverlayOnly,
	...props
}: UploadDropzoneProps) => {
	const {getInputProps, getRootProps, isDragActive, open} =
		useUploaderDropzoneProvider();

	const ownerState = {classes: _classes, className};

	const classes = useUtilityClasses(ownerState);

	const rootProps = disabled ? {} : getRootProps();

	const showSplash = children == null && !useDragOverlayOnly;
	const showDragOverlay = isDragActive && !showSplash;

	return (
		<UploadDropzoneRoot
			className={clsx(classes.root, className, {
				dragEntered: isDragActive,
			})}
			{...rootProps}
			{...props}
		>
			<input {...getInputProps()} />

			{showSplash ? (
				<UploadDropZoneSplashWrapper>
					<UploadDropzoneSplash
						className={classes.splash}
						enhancedTitle
						title={title ?? UploadFilesDropZoneLabel.CHOOSE_FILES_OR_DROP_HERE}
						icon={icon ?? faCloudUpload}
						iconColor={isDragActive ? 'primary' : undefined}
						message={fileLimitationText}
						actionButtonLabel={
							selectFileLabel ?? UploadFilesDropZoneLabel.SELECT_FILES
						}
						onActionButtonClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
							open();
						}}
					/>
				</UploadDropZoneSplashWrapper>
			) : showDragOverlay ? (
				<UploadDropZoneDragOverlayWrapper>
					<UploadDropzoneDragOverlay icon={icon ?? faCloudUpload} />
				</UploadDropZoneDragOverlayWrapper>
			) : null}
			{children}
		</UploadDropzoneRoot>
	);
};
